import { useEffect, useRef } from "react";
import MonacoEditor from "@monaco-editor/react";
import { ApiRequest } from "GlobalFunctions";
import { 
    cleanupMonacoResources, 
    configureMonacoEnvironment, 
    setupCountrySuggestions, 
    setupCartSuggestions,
    configurePennicodeLanguage // Import the new function
} from "./utils/monaco";
import { InputLabel, TextField } from "@mui/material";

const defaultValues = {
    logic_id: "",
    logic_name: "",
    conditions: {
        code: '' 
    },
};

export default function Custom(props: any) {
    const { 
        setLogicType,
        logicValues,
        setLogicvalues
    } = props;

    // References to manage the editor and its resources
    const countriesRef = useRef<any>([]);
    const monacoRef = useRef<any>(null);
    const editorRef = useRef<any>(null);
    const disposablesRef = useRef<any[]>([]);

    // Initialize default values and configure cleanup
    useEffect(() => {
        // Initialize with default values if new
        if (!logicValues.logic_id) {
            setLogicvalues(defaultValues);
        }
        
        // Cleanup function when the component unmounts
        return () => {
            // Only dispose of editor-specific resources, not the global providers
            if (disposablesRef.current?.length) {
                disposablesRef.current.forEach(disposable => {
                    if (disposable?.dispose) {
                        try { 
                            disposable.dispose(); 
                        } catch (e) { 
                            console.warn('Error releasing resource:', e); 
                        }
                    }
                });
                disposablesRef.current = [];
            }
        };
    }, []);

    // Load country data
    useEffect(() => {
        ApiRequest({
            url: '/countries/getCountries',
            method: 'GET',
            setResponse: (response: any) => {
                countriesRef.current = response;
                
                // Configure suggestions if Monaco is already available
                if (monacoRef.current) {
                    const disposable = setupCountrySuggestions(monacoRef.current, countriesRef.current);
                    if (disposable) {
                        disposablesRef.current.push(disposable);
                    }
                }
            }
        });
    }, []);
    
    // Initial configuration when mounting the editor
    const handleEditorDidMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
        
        // Configure pennicode language to inherit JavaScript features
        configurePennicodeLanguage(monaco);
        
        // Configure country suggestions
        const countryDisposable = setupCountrySuggestions(monaco, countriesRef.current);
        if (countryDisposable) {
            disposablesRef.current.push(countryDisposable);
        }
        
        // Configure custom suggestions for cart
        const cartDisposables = setupCartSuggestions(monaco);
        if (cartDisposables) {
            if (Array.isArray(cartDisposables)) {
                disposablesRef.current.push(...cartDisposables);
            } else {
                disposablesRef.current.push(cartDisposables);
            }
        }
        
        // Add shortcut to test autocompletion
        const action = editor.addAction({
            id: 'test-country-autocomplete',
            label: 'Test Country Autocomplete',
            keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK],
            run: () => {
                editor.executeEdits('', [{
                    range: editor.getSelection(),
                    text: 'country.',
                    forceMoveMarkers: true
                }]);
                editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
            }
        });
        
        // Save for later cleanup
        if (action) {
            disposablesRef.current.push(action);
        }
    };

    // Configuration before mounting the editor
    const handleEditorWillMount = (monaco: any) => {
        configureMonacoEnvironment();
        
        monaco.editor.defineTheme('custom-light-theme', {
            base: 'vs',
            inherit: true,
            rules: [
                { token: 'comment', foreground: 'B3B3B3' }, // gris claro
                { token: 'string', foreground: '3fa4ff' }, // azul claro
                { token: 'number', foreground: '3fa4ff' }, // azul claro
                { token: 'keyword', foreground: 'fe31c5' }, // rosa fuerte
                { token: 'type', foreground: 'c478ff' }, // lila
                { token: 'function', foreground: 'ffb86b' }, // naranja claro
                { token: 'variable', foreground: 'c478ff' }, // lila
                { token: 'identifier', foreground: 'c478ff' }, // lila
                { token: 'property', foreground: '22b07c' }, // verde azulado
                { token: 'operator', foreground: '000000' }, // negro
            ],
            colors: {
                'editor.background': '#F7F9FB',
            }
        });
    };

    return (
        <>
            <div className="input_group">

                <div>
                    <InputLabel className="external_label">Name</InputLabel>
                    <TextField
                        fullWidth
                        label="Enter Name To Identify"
                        size="small"
                        value={logicValues.logic_name || defaultValues.logic_name}
                        onChange={(e) => setLogicvalues({ ...logicValues, logic_name: e.target.value })}
                    />
                </div>
                    

                    {/* Code editor */}
                    <div className="code_editor">
                        <MonacoEditor
                            height="100%"
                            language="pennicode"
                            value={logicValues?.conditions?.code || defaultValues.conditions.code}
                            onChange={(value) => setLogicvalues({
                                ...logicValues,
                                conditions: {
                                    code: value
                                }
                            })}
                            theme="custom-light-theme"
                            onMount={handleEditorDidMount}
                            beforeMount={handleEditorWillMount}
                            options={{
                                fontSize: 14,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                wordWrap: 'on',
                                suggestOnTriggerCharacters: true,
                                quickSuggestions: true,
                                autoClosingBrackets: 'always', // Ensure auto bracket completion
                                autoClosingQuotes: 'always',   // Ensure auto quote completion
                                matchBrackets: 'always'        // Ensure bracket matching
                            }}
                            loading={<div style={{padding: 20}}>Loading editor...</div>}
                        />
                    </div>
                </div>
        </>
    );
}